import type { PaymentRoute } from './portal'
import { fill, text } from './content'

/**
 * The one list of everything waiting on somebody's answer (D-543).
 *
 * Seven flows used to be seven lists. This is the row shape they all share; the
 * kind says which one a row is, and `myPart` says which half of the reader it
 * is for. Spec: `docs/specs/2026-09-07-one-queue-for-every-ask.md`.
 */

export type AskPart = 'owner' | 'requester' | 'studio' | 'chase'

export type AskSubject = {
  type: string
  id: string
  /**
   * What the kind knows about its subject, by name — a dancer, a class, a
   * teacher. The sentence is composed here from a content template (A-16), so
   * the server carries no English and Tonya can reword any kind's row.
   */
  fields: Record<string, string>
}

/** One thing the answerer may choose, when the kind's yes needs a choice (D-424). */
export type AskChoice = { id: string; label: string; on: string | null }

/** What the kind's yes needs from the answerer; null means a plain yes. */
/** A time a yes would book, for the row to pick a room against (D-553). */
export type AskSlot = { date: string; start: string; end: string }

/**
 * How a row answers, when a plain yes is not enough. `options` is a list to
 * choose from, with `prompt` naming the content block over it and `noInList`
 * saying the list's last entry is the kind's no (D-424). `room` is a time the
 * yes books, so the row picks a room for it; `counter` says the owner may
 * offer a different time instead (D-548).
 */
export type AskAnswer = {
  options: AskChoice[] | null
  prompt: string | null
  noInList: boolean
  room: AskSlot | null
  counter: boolean
  /** What the scheduling assistant needs to offer another time: whose weeks, and the length. */
  assist: { dancerIds: string[]; teacherStaffId: string; minutes: number } | null
  /**
   * How the lesson a yes agrees to is paid, confirmed before the yes is sent
   * (D-705). The server's route, never a sentence; the link only on the route
   * that uses it. Optional so an older payload reads as "nothing to confirm".
   */
  payment?: { route: PaymentRoute; teacherPaymentLinks: string[] } | null
}

/** Who to ring on a chase row: one person's name with that same person's number. */
export type AskContact = { name: string; phone: string }

export type AskRow = {
  id: string
  kind: string
  subject: AskSubject
  raisedBy: string
  onBehalfOf: string | null
  raisedAt: string
  happensOn: string | null
  happensAt: string | null
  myPart: AskPart
  waitingOn: string[]
  nobodyCanAnswer: boolean
  late: boolean
  tooLateOn: string | null
  tooLateAt: string | null
  canAnswer: boolean
  canWithdraw: boolean
  opens: string | null
  answer: AskAnswer | null
  contact: AskContact | null
  /** The asker may remind the people it waits on, now (D-554, slice 3). */
  canNudge?: boolean
  /** The studio date they were last reminded, by anybody (D-68). */
  nudgedOn?: string | null
  /** Whether the raiser left automatic reminders on. */
  nudges?: boolean
  /**
   * She may ask the person this is *about* again — the family, for money (D-866).
   *
   * Distinct from `canNudge`, which pokes whoever the ask waits on. Offered even
   * where the server will refuse on the weekly cooldown: the refusal is a
   * sentence she reads, and hiding the control would leave her no way to find out
   * why.
   */
  canRemindSubject?: boolean
}

export const MY_ASKS_KEY = ['asks', 'mine'] as const
export const STUDIO_ASKS_KEY = ['asks', 'studio'] as const

/**
 * Any one of these opens the studio's list.
 *
 * Mirrors the server's gate. The client checks too because the studio list
 * carries a guardian's name and number on every chase row, and a request that
 * is going to be refused is still a request for a directory of families.
 */
export const STUDIO_ASK_CAPABILITIES = [
  'ManageClasses',
  'ApproveBookings',
  'ViewAllSchedules',
  'ManageAttendance',
] as const

export async function fetchMyAsks(): Promise<AskRow[]> {
  const res = await fetch('/api/asks/mine')
  if (!res.ok) throw new Error(`Could not load your requests (${res.status})`)

  // A shape that is not a list is a failed lookup, never an empty one (D-209):
  // it must not render as "nothing waiting", and it must not take the screen
  // it sits on down with it.
  const body = await res.json()
  if (!Array.isArray(body)) throw new Error('Your requests came back in an unexpected shape')
  return body
}

/**
 * The studio's list, or `null` when this account is not one of its readers.
 *
 * A 404 is the boundary answering, not a failure (D-83, D-281): the screen shows
 * the personal half and no studio half, rather than an error over a list the
 * reader was never going to get.
 */
export async function fetchStudioAsks(): Promise<AskRow[] | null> {
  const res = await fetch('/api/asks/studio')
  if (res.status === 404) return null
  if (!res.ok) throw new Error(`Could not load the studio's requests (${res.status})`)
  return res.json()
}

/**
 * The one answer door (D-543). A refusal comes back as a problem with a
 * title a person can read — "Somebody answered first", "Already happened" —
 * and that title is what the row shows.
 */
/**
 * The one answer door. `content` is what the kind's yes carries — a claim's
 * chosen absence — and it is checked on the server at answer time (D-424).
 */
export type AskOutcome = 'yes' | 'no' | 'countered'

export async function answerAsk(
  id: string,
  outcome: AskOutcome,
  reason?: string,
  content?: string,
): Promise<void> {
  const res = await fetch(`/api/asks/${id}/answer`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ outcome, reason: reason?.trim() || null, content: content ?? null }),
  })
  if (!res.ok) throw new Error(await problemOf(res, `Could not answer that (${res.status})`))
}

/** Pokes the people an open ask is waiting on, now (D-554, slice 3). */
export async function nudgeAsk(id: string): Promise<void> {
  const res = await fetch(`/api/asks/${id}/nudge`, { method: 'POST' })
  if (!res.ok) throw new Error(await problemOf(res, `Could not send a reminder (${res.status})`))
}

/**
 * Ask the family again about a lesson still unpaid (D-866).
 *
 * The other direction from `nudgeAsk`, which pokes whoever the ask is waiting on
 * — for money that is the teacher herself. Refused with a sentence when the
 * family was asked in the last week, or when the lesson comes off account credit
 * and there is nothing to chase.
 */
export async function remindAskSubject(id: string): Promise<void> {
  const res = await fetch(`/api/asks/${id}/remind-subject`, { method: 'POST' })
  if (!res.ok) throw new Error(await problemOf(res, `Could not ask again (${res.status})`))
}

export async function withdrawAsk(id: string, reason?: string): Promise<void> {
  const res = await fetch(`/api/asks/${id}/withdraw`, {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ reason: reason?.trim() || null }),
  })
  if (!res.ok) throw new Error(await problemOf(res, `Could not take that back (${res.status})`))
}

export type AskRefBody = { type: string; id: string }

export type AskableStaff = { staffId: string; name: string }

/** Who a task can be handed to: the list the door will accept, and nobody it will refuse. */
export async function fetchAskableStaff(): Promise<AskableStaff[]> {
  const res = await fetch('/api/asks/staff')
  if (!res.ok) throw new Error(`Could not load who this can be for (${res.status})`)
  return res.json()
}

/**
 * The one raise door, for kinds a person raises by hand (D-543, D-549). The
 * kind says what the content is; this carries it as it was given.
 */
export async function raiseAsk(body: {
  kind: string
  to?: AskRefBody
  subject?: AskRefBody
  closesOn?: string | null
  content?: unknown
  /** Remind whoever is asked if they have not answered (D-554). Omitted means yes. */
  nudges?: boolean
}): Promise<string> {
  const res = await fetch('/api/asks', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(body),
  })
  if (!res.ok) throw new Error(await problemOf(res, `Could not raise that (${res.status})`))
  const { id } = (await res.json()) as { id: string }
  return id
}

async function problemOf(res: Response, fallback: string): Promise<string> {
  try {
    const body = (await res.json()) as { title?: string; detail?: string }
    if (body.title && body.detail) return `${body.title}. ${body.detail}`
    return body.title || body.detail || fallback
  } catch {
    return fallback
  }
}

export type AskGroups = {
  waitingOnYou: AskRow[]
  chase: AskRow[]
  youAreWaitingOn: AskRow[]
}

/**
 * The three groups the screen shows, from the two lists.
 *
 * The server keeps the lists disjoint — `/mine` is what this person holds or
 * raised, `/studio` is what the studio holds or may step into — so a row is in
 * one group. Deduplicated on (kind, id) anyway, because a row rendered twice
 * would read as two things waiting.
 */
export function groupAsks(mine: readonly AskRow[], studio: readonly AskRow[] | null): AskGroups {
  const theStudio = studio ?? []

  /*
   * **A studio row the buckets do not name is shown, never dropped** (D-773).
   *
   * This filtered the studio list to `studio` and `chase` and let anything
   * else fall out. One kind labelled its rows `owner` when the answer had
   * fallen to the studio — a row she could answer, was emailed about, and
   * which then appeared on no screen at all. A list that silently discards a
   * row is a list that cannot be wrong out loud, and this one had been
   * discarding them in production.
   *
   * So the fallthrough goes where somebody will see it. The row carries its
   * own sentence about who it is waiting on, so an unexpected part reads as a
   * thing to do rather than as furniture — and the one thing it can never do
   * again is vanish.
   */
  const mayAct = theStudio.filter((r) => r.myPart !== 'chase')

  return {
    waitingOnYou: dedupe([...mine.filter((r) => r.myPart === 'owner'), ...mayAct]).sort(soonestFirst),
    chase: dedupe(theStudio.filter((r) => r.myPart === 'chase')).sort(soonestFirst),
    youAreWaitingOn: dedupe(mine.filter((r) => r.myPart === 'requester')).sort(soonestFirst),
  }
}

function dedupe(rows: AskRow[]): AskRow[] {
  const seen = new Set<string>()
  return rows.filter((r) => {
    const key = `${r.kind}:${r.id}`
    if (seen.has(key)) return false
    seen.add(key)
    return true
  })
}

/** Dated rows first, soonest first; undated rows after, oldest raised first. */
function soonestFirst(a: AskRow, b: AskRow): number {
  const day = (a.happensOn ?? '9999-12-31').localeCompare(b.happensOn ?? '9999-12-31')
  if (day !== 0) return day
  // A dated row with no time (a task due that day) sorts after that day's
  // lessons, as the server orders it.
  const time = (a.happensAt ?? '99:99:99').localeCompare(b.happensAt ?? '99:99:99')
  if (time !== 0) return time
  return a.raisedAt.localeCompare(b.raisedAt)
}

/**
 * The content-block key for a kind's on-screen name.
 *
 * Kind keys are hyphenated (`cover-stand-in`); content keys are dotted camel
 * case (`asks.kind.coverStandIn`), so this is the one place the two meet.
 */
export function kindContentKey(kind: string, part: 'name' | 'subject' = 'name'): string {
  const camel = kind.replace(/-([a-z0-9])/g, (_, c: string) => c.toUpperCase())
  return part === 'name' ? `asks.kind.${camel}` : `asks.subject.${camel}`
}

/** What a kind is called until Tonya renames it. */
export const KIND_NAMES: Record<string, string> = {
  cover: 'Cover',
  'cover-stand-in': 'Stand-in approval',
  'level-up': 'Level-up makeup',
  'banked-level': 'Banked class, out of level',
  'makeup-claim': 'Makeup to name',
  'private-lesson': 'Private lesson',
  'private-lesson-time': 'Private lesson, time offered',
  'private-confirm': 'Private lesson to agree to',
  'long-booking': 'Long booking',
  'routine-consent': 'Routine consent',
  task: 'Task',
  timesheet: 'Timesheet entry',
  'room-hold': 'Weekend room hold',
}

/**
 * How each kind describes its subject until Tonya rewords it.
 *
 * The field names are the contract with the server's kind reader; a field the
 * template names and the server did not send is left visible as `{{field}}`,
 * which is the dispatcher's rule too — obviously broken beats almost right.
 */
export const SUBJECT_TEMPLATES: Record<string, string> = {
  cover: '{{absent}} out of {{class}}',
  'cover-stand-in': '{{standIn}} to cover {{class}} for {{absent}}',
  'level-up': '{{dancer}} into {{class}}, making up {{missed}}',
  'banked-level': '{{dancer}} - does her banked class cover {{class}}?',
  'makeup-claim': '{{dancer}} with {{teacher}}, absence still to name',
  'private-lesson': '{{dancer}} with {{teacher}}',
  'private-lesson-time': '{{teacher}} offers this instead of {{asked}} for {{dancer}}',
  'private-confirm': '{{teacher}} arranged this for {{dancer}}',
  'long-booking': '{{teacher}}, {{minutes}} minutes with {{dancers}}',
  'routine-consent': '{{dancers}} making up {{routine}} with {{takenBy}}',
  task: '{{title}}',
  'room-hold': '{{teacher}} would like {{room}} {{when}}',
}

type Content = Parameters<typeof text>[0]

/** The sentence for a row's subject, from the kind's template. */
export function subjectSentence(row: AskRow, content: Content): string {
  return fill(
    content,
    kindContentKey(row.kind, 'subject'),
    row.subject.fields,
    SUBJECT_TEMPLATES[row.kind] ?? Object.values(row.subject.fields).join(', '),
  )
}

/** What a kind is called, from content or the default. */
export function kindName(kind: string, content: Content): string {
  return text(content, kindContentKey(kind), KIND_NAMES[kind] ?? kind)
}
